import React from 'react';
import { Calendar } from 'lucide-react';
import DatePicker from './DatePicker';

// ─────────────────────────────────────────────────────────────────────────────
// DateRangePicker — sélection d'une période (début → fin) avec presets rapides
// ─────────────────────────────────────────────────────────────────────────────
// Composé de 2 DatePicker + une rangée de boutons preset :
//   Aujourd'hui · Hier · 7 jours · 30 jours · Ce mois · Mois dernier
//
// Usage :
//   const [debut, setDebut] = useState(today);
//   const [fin,   setFin]   = useState(today);
//   <DateRangePicker debut={debut} fin={fin} onChange={(d, f) => { setDebut(d); setFin(f); }} />
// ─────────────────────────────────────────────────────────────────────────────

// Helpers de manipulation de dates
const toISO = (d) => d.toISOString().split('T')[0];
const today = () => new Date();
const ago = (jours) => {
  const d = new Date();
  d.setDate(d.getDate() - jours);
  return d;
};
const debutMois = (offsetMois = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMois, 1);
  return d;
};
const finMois = (offsetMois = 0) => {
  const d = new Date();
  d.setMonth(d.getMonth() + offsetMois + 1, 0);
  return d;
};

// Définition centralisée des presets
const PRESETS = [
  { id: 'today',     label: 'Aujourd\'hui', range: () => [toISO(today()),     toISO(today())] },
  { id: 'yesterday', label: 'Hier',         range: () => [toISO(ago(1)),      toISO(ago(1))] },
  { id: '7d',        label: '7 jours',      range: () => [toISO(ago(6)),      toISO(today())] },
  { id: '30d',       label: '30 jours',     range: () => [toISO(ago(29)),     toISO(today())] },
  { id: 'monthThis', label: 'Ce mois',      range: () => [toISO(debutMois(0)), toISO(today())] },
  { id: 'monthLast', label: 'Mois dernier', range: () => [toISO(debutMois(-1)), toISO(finMois(-1))] },
];

export default function DateRangePicker({
  debut,
  fin,
  onChange,
  labelDebut = 'Du',
  labelFin = 'Au',
  className = '',
}) {
  // Détecte quel preset est actuellement sélectionné (pour highlight visuel)
  const presetActif = PRESETS.find(p => {
    const [d, f] = p.range();
    return d === debut && f === fin;
  });

  return (
    <div className={`space-y-2 ${className}`}>
      {/* Boutons preset */}
      <div className="flex items-center gap-1.5 flex-wrap">
        <Calendar size={14} className="text-yellow-400 mr-0.5" />
        {PRESETS.map(p => {
          const actif = presetActif?.id === p.id;
          return (
            <button
              type="button"
              key={p.id}
              onClick={() => {
                const [d, f] = p.range();
                onChange(d, f);
              }}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors border ${
                actif
                  ? 'bg-yellow-500 text-black border-yellow-500'
                  : 'bg-gray-800 text-gray-300 border-gray-700 hover:bg-gray-700 hover:text-white'
              }`}
            >
              {p.label}
            </button>
          );
        })}
      </div>

      {/* Deux date pickers — début et fin */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <DatePicker
          label={labelDebut}
          value={debut}
          max={fin || undefined}   /* empêche début > fin */
          onChange={(e) => onChange(e.target.value, fin)}
        />
        <DatePicker
          label={labelFin}
          value={fin}
          min={debut || undefined} /* empêche fin < début */
          onChange={(e) => onChange(debut, e.target.value)}
        />
      </div>
    </div>
  );
}
